//Library Imports
import { FC, useEffect, memo } from "react";
import { RootState } from "store";
import { useSelector } from "react-redux";
import { Col, Row } from "antd";
import { useParams } from "react-router-dom";
import styled from "styled-components";

//Local Imports
import { TAB_AUDIENCE } from "constants/paths";
import ButtonRightIcon from "modules/Common/Components/ButtonRightIcon";
import Add from "../../../../assets/img/icons/addWhite.svg";
import Import from "../../../../assets/img/icons/Import.svg";
import ButtonRightIconGreen from "modules/Common/Components/ButtonRightIconGreen";
import FloatingNewListAsider from "../Lists/FloatingNewListAsider";
import ImportListFlow from "../Import/importListFlow";
import ToggleView from "modules/Common/Components/ToggleView";
import AudienceSearchTab from "./AudienceSearchTab";
import { removeGillRoyIcon } from "utils";
import TotalCountLoader from "modules/Common/Components/TotalCountLoader";

export type AudienceParams = {
  page?: string;
  tab?: string;
  subTab?: string;
};

interface IAudienceHeader {
  showNewListAsider?: boolean;
  setShowNewListAsider?: (arg0: boolean) => void;
  showImportListAsider: boolean;
  setShowImportListAsider: (arg0: boolean) => void;
  isListView?: boolean;
  setIsListView?: (value: boolean) => void;
  sortByFilter: number;
  setSortByFilter: (value: number) => void;
}

const AudienceHeader: FC<IAudienceHeader> = ({
  setShowNewListAsider,
  showImportListAsider,
  setShowImportListAsider,
  isListView,
  setIsListView,
  sortByFilter,
  setSortByFilter,
}) => {
  // Local States
  const { page, tab } = useParams<AudienceParams>();
  const isList = page === TAB_AUDIENCE.LISTS; // if list view then show create buttons
  const isDetails = tab === TAB_AUDIENCE.VIEW; // if list details view page

  // ReduxStates
  const totalLists: number = useSelector((state: RootState) => state.audienceLists.totalLists);
  const searchTerm: string = useSelector((state: RootState) => state.audienceLists.searchTerm);
  const totalSearchedLists: number = useSelector((state: RootState) => state.audienceLists.totalSearchedLists);
  const listItemAddedSuccess: boolean = useSelector((state: RootState) => state.audienceLists.addList.success); // when list created show success and close the sidebar
  const searchedLoading: boolean = useSelector((state: RootState) => state.audienceLists.searchedLoading);
  const loading: boolean = useSelector((state: RootState) => state.audienceLists.loading);

  // Component Logic
  useEffect(() => {
    if (listItemAddedSuccess) {
      setShowNewListAsider && setShowNewListAsider(false);
    }
  }, [listItemAddedSuccess]);


  const setShowImportListAsiderFunc = () => {
    setShowImportListAsider(true)
    if (window.location.pathname.startsWith("/audiences")) {
      removeGillRoyIcon()
    }
  }

  return (
    <Wrapper>
      {!isDetails && isList ? (
        <>
          <div className="d-flex flex-column justify-content-between">
            <div className="pb-2 px-2">
              <AudienceSearchTab
                page={page}
                isProfiled={false}
              />
            </div>
            <ul className="select-list d-flex justify-content-between w-100">
              <li>
                <ul>
                  <li>
                    <ToggleView isListView={isListView} setIsListView={setIsListView} />
                  </li>
                </ul>
              </li>
              <li>
                <ul>
                  <li>
                    <div className="total-lists">
                      {searchTerm.length > 0
                        ? totalSearchedLists === 0 && searchedLoading
                          ? <TotalCountLoader width={200} height={30} />
                          : `${totalSearchedLists} list(s)`
                        : totalLists === 0 && loading
                          ? <TotalCountLoader width={200} height={30} />
                          : `${totalLists} list(s)`}
                    </div>
                  </li>
                  <li>
                    <Row gutter={24} className="mb-3">
                      <Col className="gutter-row" span="12" style={{ paddingLeft: "0" }}></Col>
                      <Col className="gutter-row" span="12" style={{ marginTop: "-3px" }}>
                        <div className="d-flex flex-row-reverse ant-space ant-space-horizontal ant-space-align-center" style={{ height: "100%" }}>
                          <ButtonRightIcon
                            label="Import a list"
                            icon={Import}
                            onClick={setShowImportListAsiderFunc}
                            outlineClass="auto-width"
                          />

                          <span className="me-3 58">
                            <FloatingNewListAsider idprefix="main-asider">
                              <ButtonRightIconGreen label="Create new list" icon={Add} classIcon="plus15" />
                            </FloatingNewListAsider>
                          </span>
                        </div>
                      </Col>
                    </Row>
                  </li>
                </ul>
              </li>
            </ul>
          </div>
        </>
      ) : null}

      <ImportListFlow
        showImportListAsider={showImportListAsider}
        setShowImportListAsider={setShowImportListAsider}
      />
    </Wrapper>
  );
};

export default memo(AudienceHeader);

const Wrapper = styled.div`
  .opacity-1 {
    opacity: 1 !important;
  }
  .dropdown-options {
    background: #fff;
    position: absolute;
    top: 40px;
    left: 0px;
    z-index: 9;
    min-width: 100%;
    width: auto;
    border-radius: 8px;
    width: 200px;
    transition-duration: 0.5s;
    overflow: hidden;
    height: 0px;
    opacity: 0;
    box-shadow: 0px 5px 14px 0px rgb(0 0 0 / 28%);
    &.show {
      height: auto;
      opacity: 1;
    }
    .dropdown-options-inner {
      border: 1px solid #def6e6;
      box-shadow: 0 0 40px 0 rgb(0 0 0 / 28%);
      background: #fff;
      border-radius: 8px;
      float: left;
      width: 100%;
      .message-limit {
        float: left;
        width: 100%;
        padding: 15px;
        text-align: center;
        font-size: 16px;
        font-weight: 500;
        font-family: "Roboto", sans-serif;
        color: #ffa112;
        background: #ebf5ee;
      }
      .dropdown-header {
        background: #def6e6;
        color: #27ce0a;
        font-weight: 500;
        font-size: 16px;
        line-height: 40px;
        font-family: "Roboto", sans-serif;
        padding: 10px 16px 0 16px;
        border-radius: 8px 8px 0 0;
        float: left;
        width: 100%;
        svg {
          height: 12px;
          width: 16px;
          margin-right: 12px;
        }
        .sub-header {
          border: 1px solid #dadada;
          padding: 10px;
          background: #f7f7f7;
          float: left;
          margin: 10px -17px 0 -17px;
          width: calc(100% + 34px);
        }
      }
      .dropdown-wrapper {
        float: left;
        width: 100%;
        max-height: 450px;
        overflow: auto;
        /* &::-webkit-scrollbar {
        display: none;
      } */
        &::-webkit-scrollbar {
          width: 5px;
          height: 5px;
          border-radius: 5px;
        }
        /* Track */
        &::-webkit-scrollbar-track {
          background: #fff;
          margin-top: 10px;
          margin-bottom: 15px;
        }
        /* Handle */
        &::-webkit-scrollbar-thumb {
          background: #001760;
        }
        /* Handle on hover */
        &::-webkit-scrollbar-thumb:hover {
          background: #efefef;
        }
        .ant-checkbox:hover::after,
        .ant-checkbox-wrapper:hover .ant-checkbox::after {
          visibility: hidden;
        }
      }
      .dropdown-top-filters {
        padding: 16px 16px 16px 16px;
        border-bottom: 1px solid #dadada;
        float: left;
        width: 100%;
        .select-text-style {
          font-size: 14px;
          font-weight: 500;
          font-family: "Roboto", sans-serif;
          .grey {
            color: #c1c1c1;
            cursor: pointer;
          }
          .green {
            color: #1ec252;
            cursor: pointer;
          }
        }
        h4 {
          color: #424b50;
          font-size: 14px;
          font-weight: 500;
          font-family: "Roboto", sans-serif;
          margin: 0;
          line-height: 40px;
          width: 100%;
          float: left;
        }
        ul {
          width: 100%;
          float: left;
          display: block;
          li {
            list-style-type: none;
            margin: 5px 0;
            width: 100%;
            display: block;
            float: left;
            label {
              font-family: "Roboto", sans-serif;
              color: #7e7e7e;
              font-size: 16px;
              font-weight: 500;
              cursor: pointer;
              display: block;
              padding: 0px 0;
              float: left;
              &:hover {
                color: #27ce0a;
              }
              .ant-checkbox-wrapper {
                display: inline-block;
                float: left;
                &::after {
                  display: none;
                }
              }
              > div {
                /* margin-top: -2px; */
                float: left;
                margin: 8px 12px;
              }
            }
          }
        }
      }
      .dropdown-body {
        padding: 0 16px;
        width: 100%;
        float: left;
        ul {
          width: 100%;
          float: left;
          padding: 20px 20px 20px 0px;
          list-style-type: none;
          li {
            width: 100%;
            float: left;
            padding: 0;
            margin: 0;
            .label {
              font-family: "Roboto", sans-serif;
              color: #7e7e7e;
              font-size: 16px;
              font-weight: 500;
              cursor: pointer;
              display: block;
              padding: 5px 0;
              line-height: 30px;
              white-space: nowrap;
              text-overflow: ellipsis;
              word-break: keep-all;
              overflow: hidden;
              width: 165px;
              min-width: 165px;
              transition-duration: 0.5s;
            }
            .tick {
              opacity: 0;
              transition-duration: 0.5s;
              margin-right: 13px;
              float: left;
              svg {
                height: 12px;
                width: 16px;
                vertical-align: baseline;
              }
            }
            .current {
              color: #27ce0a;
              opacity: 1;
            }
          }
        }
      }
    }
  }
  .dropdown-selecter {
    border: 1px solid #dadada;
    min-width: 275px;
    background: #fff;
    border-radius: 8px;
    cursor: pointer;
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    align-items: center;
    padding-left: 10px;
    padding-right: 2px;
    height: 38px;
    @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
      min-width: 200px;
      border-radius: 6px;
      height: 30px;
    }
    &:focus,
    &:active,
    &.active {
      box-shadow: 0 0 0 0.1rem rgb(40 167 69 / 50%);
    }
    .label {
      color: #7e7e7e;
      font-size: 16px;
      font-weight: 500;
      font-family: "Roboto", sans-serif;
      flex-wrap: wrap;
      padding: 2px 8px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      max-width: 200px;
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        font-size: 12px;
      }
      img {
        margin-top: -3px;
        @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
          width: 15px;
        }
      }
    }
    .seprator {
      -webkit-align-self: stretch;
      -ms-flex-item-align: stretch;
      align-self: stretch;
      background-color: hsl(0, 0%, 80%);
      margin-bottom: 8px;
      margin-top: 8px;
      width: 1px;
      box-sizing: border-box;
      + .p-2 {
        @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
          padding-top: 3px !important;
          padding-bottom: 3px !important;
        }
      }
    }
  }
  .label {
    color: #7e7e7e;
    font-size: 16px;
    font-weight: 500;
    font-family: "Roboto", sans-serif;
    flex-wrap: wrap;
    padding: 2px 8px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 200px;
    @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
      font-size: 12px;
    }
    img {
      margin-top: -3px;
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        width: 15px;
      }
    }
  }
  ul.select-list,
  ul.select-list ul {
    list-style: none !important;
    padding: 0;
    margin: 0;
    display: flex;
    flex-wrap: wrap;
    &.z-index-9 {
      div[class$="-container"] {
        z-index: 8 !important;
      }
    }
    li {
      margin: 5px 5px;
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        margin: 5px 3px;
      }
      .total-lists {
        font-size: 14px;
        font-family: "Roboto Slab", serif;
        font-weight: 500;
        padding-top: 2px;
        color: #5e7079;
        @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
          padding-top: 0px;
        }
      }
      div[class$="-control"] {
        max-width: 320px;
        @media all and (max-width: 1439px) {
          max-width: 200px;
        }
      }
      .list-view {
        @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
          min-width: 36px;
          width: 36px;
          height: 31px;
          border-top-left-radius: 6px;
          border-bottom-left-radius: 6px;
          img {
            width: 18px;
          }
        }
      }
      .card-view {
        @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
          min-width: 36px;
          width: 36px;
          height: 31px;
          border-top-right-radius: 6px;
          border-bottom-right-radius: 6px;
          img {
            width: 18px;
          }
        }
      }
    }
  }
`;
